import React, { useRef, useState, useEffect } from 'react';
import '../../styles/sections/bts/BehindTheScenesSection.css';

const BehindTheScenesSection = ({
  title = 'DETRAS DE ESCENA',
  subtitle = 'Nuestro día a día',
  images = [],
  className = '',
  animationDuration = 60,
  pauseOnHover = true
}) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startTranslate, setStartTranslate] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const speedRef = useRef(0.3);
  // Refs para valores que cambian frecuentemente en touch events
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startTranslateRef = useRef(0);

  // Duplicar imágenes para crear el efecto de loop infinito
  const duplicatedImages = [...images, ...images];

  // Función para obtener el ancho de un set de imágenes (la mitad del track)
  const getSingleSetWidth = () => {
    if (!trackRef.current) return 0;
    return trackRef.current.scrollWidth / 2;
  };

  // Calcular velocidad basada en animationDuration
  // animationDuration es en segundos, queremos que recorra el ancho de un set en ese tiempo
  // A 60fps, necesitamos calcular px por frame
  const calculateSpeed = () => {
    if (!trackRef.current) return 0.5;
    const singleSetWidth = getSingleSetWidth();
    if (singleSetWidth === 0) return 0.5;
    // Calcular cuántos píxeles por frame necesitamos para completar un set en animationDuration segundos
    const fps = 60;
    const totalFrames = animationDuration * fps;
    return singleSetWidth / totalFrames;
  };


  // Actualizar velocidad cuando cambia el ancho del track o animationDuration
  useEffect(() => {
    if (trackRef.current) {
      speedRef.current = calculateSpeed();
    }
  }, [animationDuration, images.length]);

  // Animación automática usando requestAnimationFrame
  useEffect(() => {
    if (isDragging || isPaused || !trackRef.current) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const animate = () => {
      if (!trackRef.current || isDragging || isPaused) return;
      
      setCurrentTranslate((prev) => {
        const singleSetWidth = getSingleSetWidth();
        if (singleSetWidth === 0) return prev;
        
        // Actualizar velocidad dinámicamente
        speedRef.current = calculateSpeed();
        const newPosition = prev - speedRef.current;
        // Si llegamos al final de un set, resetear al inicio (efecto infinito)
        if (newPosition <= -singleSetWidth) {
          return 0;
        }
        return newPosition;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDragging, isPaused, animationDuration]);

  // Aplicar el transform al track
  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(${currentTranslate}px)`;
    }
  }, [currentTranslate]);

  const handleMouseDown = (e) => {
    if (!containerRef.current || !trackRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    isDraggingRef.current = true;
    setStartX(e.pageX);
    startXRef.current = e.pageX;
    // Obtener la posición actual del transform
    const transform = trackRef.current.style.transform;
    const match = transform.match(/translateX\(([^)]+)\)/);
    const currentPos = match ? parseFloat(match[1]) : currentTranslate;
    setStartTranslate(currentPos);
    startTranslateRef.current = currentPos;
    setCurrentTranslate(currentPos);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !trackRef.current) return;
    e.preventDefault();
    // Reducir sensibilidad del drag (de 1.5 a 0.8)
    // Si arrastro hacia la derecha (deltaX positivo), el contenido debe seguir el mouse (moverse hacia la derecha)
    // Para que el contenido se mueva hacia la derecha, translateX debe ser más positivo
    // Por eso sumamos: startTranslate + deltaX
    const deltaX = (e.pageX - startX) * 0.8;
    const newPosition = startTranslate + deltaX;
    const singleSetWidth = getSingleSetWidth();
    
    if (singleSetWidth === 0) {
      setCurrentTranslate(newPosition);
      return;
    }
    
    // Normalizar para el loop infinito
    let normalized = newPosition;
    if (normalized <= -singleSetWidth) {
      normalized = normalized + singleSetWidth;
    } else if (normalized > 0) {
      normalized = normalized - singleSetWidth;
    }
    setCurrentTranslate(normalized);
  };

  const stopDrag = () => {
    setIsDragging(false);
    isDraggingRef.current = false;
    // Reanudar animación después de un pequeño delay
    setTimeout(() => {
      setIsPaused(false);
    }, 100);
  };

  const handleTouchStart = (e) => {
    if (!containerRef.current || !trackRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    isDraggingRef.current = true;
    const touchX = e.touches[0].pageX;
    setStartX(touchX);
    startXRef.current = touchX;
    const transform = trackRef.current.style.transform;
    const match = transform.match(/translateX\(([^)]+)\)/);
    const currentPos = match ? parseFloat(match[1]) : currentTranslate;
    setStartTranslate(currentPos);
    startTranslateRef.current = currentPos;
    setCurrentTranslate(currentPos);
  };

  // Manejar todos los eventos touch con passive:false para poder usar preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onTouchMove = (e) => {
      if (!isDraggingRef.current || !trackRef.current) return;
      e.preventDefault();
      // Reducir sensibilidad del drag (de 1.5 a 0.8)
      const deltaX = (e.touches[0].pageX - startXRef.current) * 0.8;
      const newPosition = startTranslateRef.current + deltaX;
      const singleSetWidth = getSingleSetWidth();
      
      if (singleSetWidth === 0) {
        setCurrentTranslate(newPosition);
        return;
      }
      
      // Normalizar para el loop infinito
      let normalized = newPosition;
      if (normalized <= -singleSetWidth) {
        normalized = normalized + singleSetWidth;
      } else if (normalized > 0) {
        normalized = normalized - singleSetWidth;
      }
      setCurrentTranslate(normalized);
    };

    const onTouchEnd = () => {
      stopDrag();
    };

    // Registrar con passive:false para poder usar preventDefault
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: false });
    container.addEventListener('touchcancel', onTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []); // Sin dependencias - usa refs para valores que cambian

  return (
    <section className={`bts-section ${className}`}>
      <div className="bts-section__container">
        <div className="bts-section__header">
          <h2 className="bts-section__title">{title}</h2>
          <p className="bts-section__subtitle">{subtitle}</p>
        </div>

        <div
          ref={containerRef}
          className={`bts-section__slider ${pauseOnHover ? 'bts-section__slider--pause-hover' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDrag}
          onTouchStart={handleTouchStart}
          // Touch events se manejan con event listeners nativos para poder usar passive:false
          onMouseEnter={() => pauseOnHover && !isDragging && setIsPaused(true)}
          onMouseLeave={(e) => {
            stopDrag();
            if (pauseOnHover && !isDragging) {
              setIsPaused(false);
            }
          }}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <div
            ref={trackRef}
            className="bts-section__track"
          >
            {duplicatedImages.map((src, idx) => (
              <div key={`bts-image-${idx}`} className="bts-section__card">
                <img 
                  src={src} 
                  alt={`Behind the scenes ${(idx % images.length) + 1}`} 
                  className="bts-section__image" 
                  draggable={false} 
                  onDragStart={(e) => e.preventDefault()} 
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BehindTheScenesSection;


